import { ApolloError, gql } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import { Dropdown } from '../../../../common/src/components/Dropdown'
import { Spacer, H1, IntroText, InputLarge, IntroTextSpan, BodyTextSpan } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import {
  AddressLookupQuery,
  PmType,
  CreateFirmMutation,
  CreateFirmMutationVariables,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { addressLookupQuery } from '../../graphql/queries/queries'
import AddressChooser from '../components/AddressChooser'
import { Body, ScrollPane } from '../components/containers'

const createFirmMutation = gql`
  mutation CreateFirm(
    $pmType: PmType!
    $firmName: String!
    $pmFirm: String
    $addressId: Int!
    $salesforceVPlusObjectId: String
  ) {
    createFirm(
      pmType: $pmType
      firmName: $firmName
      pmFirm: $pmFirm
      addressId: $addressId
      salesforceVPlusObjectId: $salesforceVPlusObjectId
    ) {
      id
    }
  }
`

/**
 * Type declaration needed for `Dropdown` of `PmType`s
 */
interface ListItem {
  disabled?: boolean
  name: string
  id: number
}

class CreateFirmDetail extends React.PureComponent<
  {
    onCreate: (firmId: number) => void
  },
  {
    address: AddressLookupQuery['addressLookup'] | null
    pmType: PmType
    hasErrorFirmName: boolean
    hasErrorPmFirm: boolean
    hasErrorAddress: boolean
    hasErrorSalesforceValuePlusObject: boolean
  }
> {
  private firmNameRef = React.createRef<HTMLInputElement>()
  private pmFirm = React.createRef<HTMLInputElement>()
  private salesforceVPlusObjectId = React.createRef<HTMLInputElement>()

  constructor(props: CreateFirmDetail['props']) {
    super(props)

    this.state = {
      address: null,
      pmType: PmType.APPFOLIO,
      hasErrorFirmName: false,
      hasErrorPmFirm: false,
      hasErrorSalesforceValuePlusObject: false,
      hasErrorAddress: false,
    }
  }

  render() {
    const { pmType, address, hasErrorFirmName, hasErrorPmFirm, hasErrorSalesforceValuePlusObject, hasErrorAddress } =
      this.state

    /**
     * List of possible `PmType`s the user can choose from
     */
    const pmTypes: ListItem[] = Object.keys(PmType).map((o, i) => ({ name: o, id: i }))

    return (
      <ScrollPane>
        <Body>
          <Spacer height={24} />
          <H1>Create new firm</H1>
          <Spacer height={32} />

          <IntroText>What PM system do they use?</IntroText>
          <Spacer height={8} />
          <Dropdown
            items={pmTypes}
            selectedItemId={undefined}
            onSelectedItemChange={this.setPmType}
            placeholder={'Choose one …'}
            disabled={false}
          />
          <Spacer height={32} />

          <IntroText>
            What’s the <em>short</em> version of their firm name?
          </IntroText>
          <Spacer height={8} />
          <InputLarge hasError={hasErrorFirmName} ref={this.firmNameRef} />
          <Spacer height={32} />

          <IntroTextSpan>
            {pmType === PmType.APPFOLIO
              ? 'What’s their AppFolio vhost?'
              : 'In their PM software, what is their firm name?'}
          </IntroTextSpan>
          <Spacer width={8} />
          <BodyTextSpan>
            {pmType === PmType.APPFOLIO ? '(e.g. http://vhost.appfolio.com)' : '(e.g. “44639radco”)'}
          </BodyTextSpan>
          <Spacer height={8} />
          <InputLarge hasError={hasErrorPmFirm} ref={this.pmFirm} />
          <Spacer height={32} />

          {pmType === PmType.APPFOLIO && (
            <div>
              <IntroTextSpan>What’s their Salesforce Value+ Object ID?</IntroTextSpan>
              <Spacer width={8} />
              <BodyTextSpan>(e.g. “a1l2E00000BZJkaQAH”)</BodyTextSpan>
              <Spacer height={8} />
              <InputLarge hasError={hasErrorSalesforceValuePlusObject} ref={this.salesforceVPlusObjectId} />
              <Spacer height={32} />
            </div>
          )}

          <IntroTextSpan>What is the firm's address?</IntroTextSpan>
          <Spacer width={8} />
          <BodyTextSpan>You can usually find it on Google</BodyTextSpan>
          <Spacer height={16} />
          <AddressChooser hasError={hasErrorAddress} onChoose={this.onChooseAddressBound} />
          <Spacer height={32} />

          <IntroText>
            <pre>{address && JSON.stringify(address, null, 2)}</pre>
          </IntroText>
          <Spacer height={32} />

          {address && (
            <Button color="sky" onClick={this.createFirmBound}>
              Create Firm and Leasing Task
            </Button>
          )}
        </Body>
      </ScrollPane>
    )
  }

  /**
   * Take the PmType from the picklist and assign it to `this.state`
   */
  private setPmType = (item: ListItem | undefined) => this.setState({ pmType: item!.name as PmType })

  /**
   * Validator function makes sure firm has a name
   */
  private validateFirmName = (firmName: string) => {
    if (!firmName) {
      this.setState({ hasErrorFirmName: true })
    } else {
      this.setState({ hasErrorFirmName: false })
    }
  }

  /**
   * Validator function makes sure firm has PmInfo so it can pull units
   */
  private validatePmSyncInfo = (pmType: PmType, pmFirm: string) => {
    // firm has either has a vhost (AppFolio) OR a pmFirm (standalone) — never both
    if (!pmFirm && pmType === PmType.APPFOLIO) {
      this.setState({ hasErrorPmFirm: true })
    } else {
      this.setState({ hasErrorPmFirm: false })
    }
  }

  /**
   * Validator function makes sure AppFolio firms have correct Value+ object IDs. All AppFolio firms must have a Salesforce VPlus object on creation.
   * Salesforce docs tell us this ID will be at least 15 characters long: https://trailhead.salesforce.com/en/content/learn/modules/data_modeling/objects_intro
   */
  private validateSalesforceVPlusObjectId = (pmType: PmType, salesforceVPlusObjectId: string | undefined) => {
    const isAppfolioFirm = pmType === PmType.APPFOLIO
    const salesforceIdIsMissing = isAppfolioFirm && !salesforceVPlusObjectId
    const salesforceIdIsWrongLength = isAppfolioFirm && salesforceVPlusObjectId!.length < 15
    const salesforceIdHasWrongPrefix = isAppfolioFirm && salesforceVPlusObjectId?.startsWith('VAS')

    if (salesforceIdIsMissing || salesforceIdIsWrongLength || salesforceIdHasWrongPrefix) {
      this.setState({ hasErrorSalesforceValuePlusObject: true })

      if (salesforceIdIsWrongLength) {
        alert('Salesforce Value+ ID is wrong. Use the exact ID from the end of the URL in Salesforce.')
      }

      if (salesforceIdHasWrongPrefix) {
        alert(
          'Salesforce Value+ ID never starts with “VAS…”. Use the exact 15-digit ID from the URL in Salesforce, like … “a1l2E00000BZJkaQAH”.'
        )
      }
    } else {
      this.setState({ hasErrorSalesforceValuePlusObject: false })
    }
  }

  /**
   * Validate each field, submit full set to AppServer for creation using `createFirmMutation`
   */
  private createFirmBound = () => {
    const { pmType, address } = this.state

    const firmName = this.firmNameRef.current!.value.trim()
    const pmFirm = this.pmFirm.current!.value.trim()
    const salesforceVPlusObjectId = this.salesforceVPlusObjectId.current?.value.trim()

    this.validateFirmName(firmName)
    this.validatePmSyncInfo(pmType, pmFirm)
    this.validateSalesforceVPlusObjectId(pmType, salesforceVPlusObjectId)

    // all firms must have a validated address from Google Maps
    if (!address) {
      this.setState({ hasErrorAddress: true })
      return
    }

    const addressId = address.id

    apolloWebClient
      .mutate<CreateFirmMutation, CreateFirmMutationVariables>({
        mutation: createFirmMutation,
        variables: { pmType, firmName, pmFirm, addressId, salesforceVPlusObjectId },
      })
      .then(result => {
        const newFirmId = result.data!.createFirm.id
        return this.props.onCreate(newFirmId)
      })
      .catch(handleError)
  }

  private onChooseAddressBound = (inputAddress: string) => {
    apolloWebClient
      .query<AddressLookupQuery>({
        query: addressLookupQuery,
        variables: {
          inputAddress,
        },
      })
      .then(result => {
        if (result.data == null) {
          throw new Error('data not available')
        }

        const address = result.data.addressLookup
        this.setState({
          address,
          hasErrorAddress: address == null,
        })
      })
      .catch((err: ApolloError) => {
        handleError(err)
        this.setState({ address: null, hasErrorAddress: true })
      })
  }
}

export default CreateFirmDetail
